import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './VolunteerApplicationPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUser, faEnvelope, faPhone, faBriefcase, faGraduationCap, faClipboardList, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const VolunteerApplicationPage = () => {
    const navigate = useNavigate();
    const { category } = useParams();
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        category: category || 'developer',
        experience: '',
        motivation: '',
        availability: '',
        skills: []
    });

    const categories = {
        developer: {
            title: '개발 도우미',
            icon: '💻',
            skills: ['React', 'Node.js', 'Python', 'FastAPI', 'UI/UX', 'Database', '모바일 앱', '기타']
        },
        medical: {
            title: '의료 전문가',
            icon: '🩺',
            skills: ['외상외과', '응급의학과', '간호사', '응급구조사', '의료행정', '의료IT', '기타']
        },
        admin: {
            title: '운영 관리자',
            icon: '📊',
            skills: ['팀 리드','프로젝트 관리', '마케팅', '회계/재무', '고객 응대', '콘텐츠 제작', '데이터 분석', '기타']
        },
        sponsor: {
            title: '후원처 추천',
            icon: '🤝',
            skills: ['권역외상센터 추천', '기업 후원 연결', '의료진 니즈 파악', '지역사회 네트워킹', '기타']
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSkillToggle = (skill) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.includes(skill)
                ? prev.skills.filter(s => s !== skill)
                : [...prev.skills, skill]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.phone || !formData.motivation) {
            alert('필수 항목을 모두 입력해주세요.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.submitVolunteerApplication({
                ...formData,
                applied_at: new Date().toISOString()
            });

            if (response.success) {
                setSubmitted(true);
            } else {
                alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        } catch (error) {
            console.error('Volunteer application error:', error);
            alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="volunteer-application-page">
                <div className="application-container success-container">
                    <div className="success-icon">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h2>신청이 완료되었습니다!</h2>
                    <p className="success-message">
                        {formData.name}님, 한국외상센터지원단체의 활동에 참여해 주셔서 감사합니다.<br />
                        빠른 시일 내에 {formData.email}로 연락드리겠습니다.
                    </p>
                    <div className="success-actions">
                        <button onClick={() => navigate('/')} className="btn-home">
                            홈으로 돌아가기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentCategory = categories[formData.category] || categories.developer;

    return (
        <div className="volunteer-application-page">
            <div className="application-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <FontAwesomeIcon icon={faArrowLeft} /> 돌아가기
                </button>
            </div>

            <div className="application-container">
                <div className="application-title">
                    <span className="category-icon">{currentCategory.icon}</span>
                    <h1>{currentCategory.title} 신청</h1>
                    <p>여러분의 재능과 열정으로 생명을 구하는 일에 동참해주세요</p>
                </div>

                <form onSubmit={handleSubmit} className="application-form">
                    <div className="form-section">
                        <h3>기본 정보</h3>
                        
                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faUser} /> 이름 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="홍길동"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faEnvelope} /> 이메일 <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="example@email.com"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faPhone} /> 연락처 <span className="required">*</span>
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="010-1234-5678"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faBriefcase} /> 참여 분야
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                            >
                                <option value="developer">개발 도우미</option>
                                <option value="medical">의료 전문가</option>
                                <option value="admin">운영 관리자</option>
                                <option value="sponsor">후원처 추천</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>전문 분야</h3>
                        <p className="section-desc">해당하는 분야를 모두 선택해주세요</p>
                        
                        <div className="skills-grid">
                            {currentCategory.skills.map(skill => (
                                <label key={skill} className="skill-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={formData.skills.includes(skill)}
                                        onChange={() => handleSkillToggle(skill)}
                                    />
                                    <span>{skill}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>상세 정보</h3>
                        
                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faGraduationCap} /> 관련 경험
                            </label>
                            <textarea
                                name="experience"
                                value={formData.experience}
                                onChange={handleInputChange}
                                placeholder="관련 경험이나 경력을 자유롭게 작성해주세요"
                                rows="4"
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                <FontAwesomeIcon icon={faClipboardList} /> 참여 동기 <span className="required">*</span>
                            </label>
                            <textarea
                                name="motivation"
                                value={formData.motivation}
                                onChange={handleInputChange}
                                placeholder="어떤 마음으로 참여하시게 되었는지 들려주세요"
                                rows="4"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                참여 가능 시간
                            </label>
                            <select
                                name="availability"
                                value={formData.availability}
                                onChange={handleInputChange}
                            >
                                <option value="">선택해주세요</option>
                                <option value="주 1-2시간">주 1-2시간</option>
                                <option value="주 3-5시간">주 3-5시간</option>
                                <option value="주 6-10시간">주 6-10시간</option>
                                <option value="주 10시간 이상">주 10시간 이상</option>
                                <option value="프로젝트 단위">프로젝트 단위로 참여</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="btn-cancel"
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '신청 중...' : '신청하기'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VolunteerApplicationPage;